<template>
	<BaseWebshopCart v-slot="{cart, total}">
		<aside class="cart-box">
			<div class="cart-box-body">
				<div v-if="mode == 'cart'">
					<WebshopCouponForm />
					<div class="cart-box-total">
						<div v-if="cart?.api_id" class="cart-box-id"><strong>ID:</strong> {{ cart.api_id }}</div>
						<div class="cart-box-totals">
							<BaseCmsLabel code="total_to_pay_cart" />: <span><BaseUtilsFormatCurrency :price="total.total_items_total" /></span>
						</div>
						<div class="cart-box-note"><BaseCmsLabel code="total_without_shipping" /></div>
						<div class="cart-box-note cart-box-loyalty" v-if="cartData?.customer && cartData.customer?.loyalty && totalLoyalty" v-html="totalLoyalty"></div>

						<BaseWebshopCartErrors v-slot="{errors, errorsItems}">
							<BaseUtilsAppUrls v-slot="{items}">
								<div v-if="errors || errorsItems || cart?.warnings?.cart?.length" class="btn btn-green btn-cart-box btn-icon-arrow btn-disabled">
									<span><BaseCmsLabel code="checkout" /></span>
									<span class="wc-box-btn-note">
										<template v-if="cart?.warnings?.cart?.length"><BaseCmsLabel code="cart_warning_next_step_note" /></template>
										<template v-else><BaseCmsLabel code="cart_next_step_note" /></template>
									</span>
								</div>
								<NuxtLink v-else :to="items.webshop_customer" @click="cartBeginCheckout()" class="btn btn-green btn-cart-box btn-icon-arrow">
									<span><BaseCmsLabel code="checkout" /></span>
								</NuxtLink>
							</BaseUtilsAppUrls>
						</BaseWebshopCartErrors>
					</div>
				</div>
				<div v-else>
					<BaseCmsLabel code="wc_sidebar_title" tag="div" class="wc-box-title" />
					<div v-if="cart?.api_id" class="cart-box-id"><strong>ID:</strong> {{ cart.api_id }}</div>

					<template v-if="props.mode == 'total_full'">
						<div class="cart-box-totals special wc-box-totals">
							<BaseCmsLabel code="wc_total_to_pay" /> <span><BaseUtilsFormatCurrency :price="total.total_items_total" /></span>
						</div>
						<div class="cart-box-totals special wc-box-totals" v-if="review">
							<BaseCmsLabel code="wc_tax" /> <span><BaseUtilsFormatCurrency :price="total.tax" /></span>
						</div>
						<div class="cart-box-totals special wc-box-totals">
							<BaseCmsLabel code="shipping" />:
							<span v-if="total.shipping == 0" class="green"><BaseCmsLabel code="free" /></span>
							<span v-else><BaseUtilsFormatCurrency :price="total.shipping" /></span>
						</div>
						<div class="cart-box-totals full wc-box-totals">
							<BaseCmsLabel code="wc_full_total_to_pay" /> <span><BaseUtilsFormatCurrency :price="total.total_regular" /></span>
						</div>
						<div v-if="giftCard" class="wc-box-gift-card">
							<div class="cart-box-totals special wc-box-totals">
								<BaseCmsLabel code="value_card" />: <span>- <BaseUtilsFormatCurrency :price="giftCard" /></span>
							</div>
							<div class="cart-box-totals special wc-box-totals">
								<strong><BaseCmsLabel code="value_card_total_for_pay" />:</strong>
								<span
									><strong><BaseUtilsFormatCurrency :price="total.total" /></strong
								></span>
							</div>
						</div>
					</template>
					<template v-else>
						<div class="cart-box-totals wc-box-totals">
							<BaseCmsLabel code="wc_total_to_pay" /> <span><BaseUtilsFormatCurrency :price="total.total_items_total" /></span>
						</div>
						<div class="cart-box-note"><BaseCmsLabel code="total_without_shipping" /></div>
					</template>

					<div class="cart-box-note cart-box-loyalty" v-if="cartData?.customer && cartData.customer?.loyalty && totalLoyalty" v-html="totalLoyalty"></div>
					<div v-interpolation>
						<BaseCmsLabel v-if="review" code="accept_terms_webshop_text" tag="div" class="wc-terms" />
					</div>

					<button v-if="checkoutLoading || creation" class="btn btn-green btn-cart-box btn-icon-arrow btn-submit btn-spinner"></button>
					<div v-else-if="(customer && !meta?.valid) || (!customer && (!meta?.valid || !formValid)) || (cart?.warnings?.cart?.length)" class="btn btn-green btn-cart-box btn-icon-arrow btn-submit btn-disabled">
						<span v-if="review"><BaseCmsLabel code="confirm_order" /></span>
						<span v-else><BaseCmsLabel code="wc_next_step" /></span>
						<span v-if="(customer && !meta?.valid) || (!customer && (!meta?.valid || !formValid) || (cart?.warnings?.cart?.length))" class="wc-box-btn-note">
							<template v-if="cart?.warnings?.cart?.length"><BaseCmsLabel code="cart_warning_next_step_note" /></template>
							<template v-else><BaseCmsLabel code="cart_next_step_note" /></template>
						</span>
					</div>
					<button v-else class="btn btn-green btn-cart-box btn-icon-arrow btn-submit" type="submit">
						<span v-if="review"><BaseCmsLabel code="confirm_order" /></span>
						<span v-else><BaseCmsLabel code="wc_next_step" /></span>
					</button>
				</div>
			</div>

			<div class="cart-box-return" @click="flyoutLabel()">
				<strong><BaseCmsLabel code="return_policy" /></strong>
				<BaseCmsLabel code="return_policy_value" />
			</div>

			<div class="cart-box-cards">
				<div class="cart-box-subtitle cart-cards-title">
					<strong><BaseCmsLabel code="safe_payment" /></strong>
				</div>
				<BaseCmsLabel class="cart-cards" code="cards" tag="div" />
			</div>
		</aside>
	</BaseWebshopCart>
</template>

<script setup>
	import {useValidateForm} from 'vee-validate';

	const webshop = useWebshop();
	const cartData = computed(() => webshop.getCartData());
	const labels = useLabels();
	const gtm = useGtmBB();
	const {emit} = useEventBus();
	const props = defineProps({
		mode: {
			type: String,
			default: '',
		},
		meta: Object,
		review: {
			type: null,
			default: false,
		},
		customer: {
			type: null,
			default: false,
		},
		invalidSubmitNote: {
			type: null,
			default: false,
		},
	});

	const { checkoutLoading, formValid, creation } = useCheckout();

	//flyout
	function flyoutLabel(title, content) {
		document.body.classList.add('flyout-page', 'cd-flyout-active');
		emit('flyoutUpdate', {'flyout_title': labels.get('return_policy_flyout_title'), 'flyout_content': labels.get('return_policy_info')});
	}

	// gift card
	const giftCard = computed(() => {
		const giftCardPayment = cartData.value?.cart?.payments?.selected?.find(el => el.widget == 'giftcard');
		if(!giftCardPayment) return null;

		const giftCardData = giftCardPayment?.giftcard_data?.find(el => el.extra_info != null);
		if(giftCardData) {
			return giftCardData.extra_info.amount_to_be_used;
		}
	});

	//loyalty code
	const totalLoyalty = computed(() => {
		let loyalty_code = cartData.value && cartData.value.customer?.loyalty ? cartData.value.customer?.loyalty : null;
		let desc = labels.get('total_with_loyalty') ? labels.get('total_with_loyalty') : null;
		if (desc && loyalty_code) {
			desc = desc.replace('%LOYALTY_CODE%', loyalty_code);
		}
		return desc;
	});

	//begin checkout
	function cartBeginCheckout() {
		gtm.beginCheckout()
	}
</script>

<style lang="less" scoped>
	* {
		--sideOffset: 40px;
	}
	@media (max-width: @t) {
		* {
			--sideOffset: 25px;
		}
	}
	@media (max-width: @m) {
		* {
			--sideOffset: 20px;
		}
		.btn { height: 54px; font-size: 16px; line-height: 1; }
		.btn-icon-arrow > span:first-child:before { top: 4px; font-size: 9px; }
	}
	.cart-box {
		width: 395px; flex-shrink: 0; background: @white; border: 1px solid @borderColor; box-shadow: 0 0 18px 0 rgba(120, 145, 169, 0.18); position: sticky; top: 85px; font-size: 15px;
		@media (max-width: @t) { width: 360px; }
		@media (max-width: @m) { width: 100%; font-size: 15px; }
	}
	.cart-box-body {
		padding: 30px var(--sideOffset); border-bottom: 1px solid @borderColor;
		@media (max-width: @m) { padding-block: 20px; }
	}
	.cart-box-title { margin-bottom: 10px; font-size: 19px; font-weight: 600; }
	.cart-box-subtitle {
		display: block; margin-bottom: 10px;
		@media (max-width: @m) { margin-bottom: 5px; }
	}
	.cart-box-id { padding-bottom: 10px; }
	.cart-box-totals {
		display: grid; grid-template-columns: repeat(2, 1fr); font-size: 19px; font-weight: 600;
		&.special { font-weight: normal; margin-bottom: 8px; }
		&.full { margin-top: 10px; padding-top: 10px; border-top: 1px solid @borderColor; font-size: 17px; }
		@media (max-width: @m) {
			font-size: 17px;
			&.special { line-height: 1.3; }
		}
		span { flex-grow: 1; text-align: right; }
	}
	.cart-box-note {
		margin-top: 10px; font-size: 13px; color: @inputTextColor; position: relative; padding-left: 21px;
		&:before { .icon-info(); font: 15px/1 @fonti; position: absolute; top: 1px; left: 0; }

		@media (max-width: @m) {
			&:before { top: 3px; }
		}
	}
	.cart-box-loyalty {
		padding-left: 0;
		&:before { content: none; }
	}
	.btn-cart-box {
		margin-top: 17px; width: 100%;
		&.btn-disabled { cursor: default; }
		&:hover .wc-box-btn-note { opacity: 1; visibility: visible; z-index: 11; }
	}

	.cart-box-return {
		display: grid; align-items: center; padding: 15px 60px 15px calc(var(--sideOffset) + 35px); color: @textColor; text-decoration: none; position: relative; border-bottom: 1px solid @borderColor; font-size: 14px; cursor: pointer;
		strong {
			font-size: 15px;
			@media (max-width: @m) { font-size: 14px; }
		}
		&:before { .icon-return(); font: 18px/1 @fonti; color: @blue; position: absolute; left: var(--sideOffset); }
		&:after { .icon-arrow-down(); font: 12px/1 @fonti; color: @blue; position: absolute; right: var(--sideOffset); .rotate(-90deg); .transition(right); }
		&:hover:after { right: 33px; }
	}

	.cart-box-cards { padding: 15px var(--sideOffset); position: relative; }
	.cart-cards-title {
		padding-left: 35px; position: relative;
		&:before { .icon-credit-card(); font: 16px/1 @fonti; color: @blue; position: absolute; top: 2px; left: 0; }

		@media (max-width: @m) {
			&:before { top: 4px; }
		}
	}
	.cart-cards {
		display: flex;
		a {
			margin: 0 3px 0 0;
			&:last-child { margin-right: 0; }
		}
	}

	// checkout specific
	.wc-box-title { margin-bottom: 10px; font-size: 19px; font-weight: 600; }
	.wc-box-btn-note {
		padding: 15px 20px 15px 50px; background: @white; border: 1px solid @red2; font-size: 14px; color: @textColor; text-align: left; position: absolute; top: 100%; left: -1px; right: -1px; opacity: 0; visibility: hidden; z-index: -11;
		&:before { .icon-available-last(); font: 18px/18px @fonti; color: @red2; right: unset; left: 20px; top: 16px; transform: none; }
		&:after { .pseudo(6px,6px); background: @white; border-left: 1px solid @red2; border-top: 1px solid @red2; top: -4px; left: 50%; transform: translateX(50%) rotate(45deg); }
		@media (max-width: @t) { display: none !important; }
	}
	.wc-box-totals { font-size: 15px; }
	.wc-terms { display: block; margin-top: 15px; font-size: 14px; line-height: 1.5; }

	//gift card
	.wc-box-gift-card { margin-top: 13px; padding: 13px 0 3px; border-top: 1px solid @borderColor; border-bottom: 1px solid @borderColor; }
</style>
